/**
 * Flex Duct Tool
 * 
 * Spline-based flexible duct placement.
 */

import { v4 as uuidv4 } from 'uuid';
import { FlexDuctElement, FlexDuctGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class FlexDuctTool extends BaseTool {
    id = 'flex_duct';
    name = 'Flex Duct';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 2) {
                this.finishPath();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'flex_duct',
                points: previewPoints,
                diameter: 0.2
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishPath() {
        if (!this.context) return;

        const controlPoints: [number, number, number][] = this.points.map(p => [p.x, p.y, p.z]);

        // Calculate accurate spline length
        const curve = new THREE.CatmullRomCurve3(this.points);
        const length = curve.getLength();

        const geometryData: FlexDuctGeometryData = {
            controlPoints,
            diameter: 200 // 200mm default
        };

        const flexDuct: FlexDuctElement = {
            id: uuidv4(),
            category: 'Flex Ducts',
            type: 'Round Flex Duct',
            parameters: {
                'Diameter': '200 mm',
                'Length': length.toFixed(2) + ' m',
                'Insulation': 'None',
                'System Type': 'Supply Air'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(flexDuct);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}
